
import { useState, useEffect, useCallback } from "react"
import Pagination from "../../Common/Pagination"
import axiosInstance from "../../../utils/axiosInstance"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { API_ENDPOINTS } from "../../../constants/config";

function AddClass({ onClose, onSuccess }) {
  const [className, setClassName] = useState("")
  const [classOrder, setClassOrder] = useState(0)
  const [error, setError] = useState(null)

  const handleSubmit = async () => {
    try {
      setError(null)
      await axiosInstance.post(API_ENDPOINTS.classes, { 
        name: className,
        order: classOrder // Include order
      })
      onSuccess()
    } catch (error) {
      setError(error.response?.data?.message || "Failed to add class")
      console.error("Error adding class:", error)
    }
  }

  return (
    <Card>
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold mb-4">Add New Class</h2>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <Input
          value={className}
          onChange={(e) => setClassName(e.target.value)}
          placeholder="Enter class name"
          className="mb-4"
        />
        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>Add Class</Button>
        </div>
      </CardContent>
    </Card>
  )
}

function EditClass({ classData, onClose, onSuccess }) {
  const [className, setClassName] = useState(classData.name)
  const [error, setError] = useState(null)

  const handleSubmit = async () => {
    try {
      setError(null)
      await axiosInstance.patch(API_ENDPOINTS.class(classData._id), {
        name: className,
      })
      onSuccess()
    } catch (error) {
      setError(error.response?.data?.message || "Failed to update class")
      console.error("Error updating class:", error)
    }
  }

  return (
    <Card>
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold mb-4">Edit Class</h2>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <Input
          value={className}
          onChange={(e) => setClassName(e.target.value)}
          placeholder="Enter class name"
          className="mb-4"
        />
        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>Update Class</Button>
        </div>
      </CardContent>
    </Card>
  )
}

export function ClassList() {
  const [classes, setClasses] = useState([])
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedClass, setSelectedClass] = useState(null)
  const [error, setError] = useState(null)
  const [isReorderMode, setIsReorderMode] = useState(false)
  const [dragIndex, setDragIndex] = useState(null)
  const [page, setPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  useEffect(() => {
    const token = localStorage.getItem("token")
    if (token) {
      axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`
    }
  }, [])

  const fetchClasses = useCallback(async () => {
    try {
      setError(null)
      const response = await axiosInstance.get(API_ENDPOINTS.classes)
      setClasses(response.data.data.classes)
    } catch (error) {
      setError(error.response?.data?.message || "Failed to fetch classes")
      console.error("Error fetching classes:", error)
    }
  }, [])

  const handleDelete = async (classId) => {
    try {
      setError(null)
      await axiosInstance.delete(API_ENDPOINTS.class(classId))
      fetchClasses()
    } catch (error) {
      setError(error.response?.data?.message || "Failed to delete class")
      console.error("Error deleting class:", error)
    }
  }

  useEffect(() => {
    fetchClasses()
  }, [fetchClasses])

  const handleDragStart = (index) => {
    setDragIndex(index)
  }

  const handleDragOver = (e, index) => {
    e.preventDefault()
    // Optional: we could add visual feedback here later
  }

  const handleDrop = (index) => {
    if (dragIndex === null || dragIndex === index) return

    const updated = [...classes]
    const [moved] = updated.splice(dragIndex, 1)
    updated.splice(index, 0, moved)
    setClasses(updated)
    setDragIndex(null)
  }

  const handleDragEnd = () => {
    setDragIndex(null)
  }

  const saveOrder = async () => {
    try {
      setError(null)
      // Update order field based on current index (1-based)
      await Promise.all(
        classes.map((cls, idx) =>
          axiosInstance.patch(API_ENDPOINTS.class(cls._id), {
            order: idx + 1,
          })
        )
      )
      setIsReorderMode(false)
      fetchClasses()
    } catch (error) {
      setError(error.response?.data?.message || "Failed to save order")
      console.error("Error saving class order:", error)
    }
  }

  return (
    <Card className="w-full">
      <CardContent className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchClasses}>
              Refresh List
            </Button>
            <Button
              variant={isReorderMode ? "destructive" : "outline"}
              onClick={() => setIsReorderMode((prev) => !prev)}
            >
              {isReorderMode ? "Cancel Reorder" : "Rearrange Order"}
            </Button>
          </div>
          <Button onClick={() => setShowAddModal(true)}>Add New Class</Button>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order</TableHead>
              <TableHead>Class Name</TableHead>
              <TableHead>Created At</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(isReorderMode ? classes : classes.slice((page - 1) * itemsPerPage, page * itemsPerPage)).map((cls, index) => (
              <TableRow
                key={cls._id}
                draggable={isReorderMode}
                onDragStart={() => isReorderMode && handleDragStart(index)}
                onDragOver={(e) => isReorderMode && handleDragOver(e, index)}
                onDrop={() => isReorderMode && handleDrop(index)}
                onDragEnd={handleDragEnd}
                className={isReorderMode ? "cursor-move" : ""}
              >
                <TableCell>{index + 1}</TableCell>
                <TableCell>{cls.name}</TableCell>
                <TableCell>{new Date(cls.createdAt).toLocaleDateString()}</TableCell>
                <TableCell>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      disabled={isReorderMode}
                      onClick={() => {
                        setSelectedClass(cls)
                        setShowEditModal(true)
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="destructive"
                      disabled={isReorderMode}
                      onClick={() => handleDelete(cls._id)}
                    >
                      Delete
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {!isReorderMode && (
          <Pagination
            currentPage={page}
            totalItems={classes.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setPage}
            onItemsPerPageChange={(newLimit) => { setItemsPerPage(newLimit); setPage(1); }}
            itemLabel="classes"
          />
        )}

        {showAddModal && (
          <AddClass
            onClose={() => setShowAddModal(false)}
            onSuccess={() => {
              setShowAddModal(false)
              fetchClasses()
            }}
          />
        )}
        {showEditModal && selectedClass && (
          <EditClass
            classData={selectedClass}
            onClose={() => {
              setShowEditModal(false)
              setSelectedClass(null)
            }}
            onSuccess={() => {
              setShowEditModal(false)
              setSelectedClass(null)
              fetchClasses()
            }}
          />
        )}

        {isReorderMode && (
          <div className="mt-4 flex items-center justify-between text-sm">
            <span className="text-gray-500">
              Drag and drop rows to change order, then click Save.
            </span>
            <Button onClick={saveOrder}>Save Order</Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}